import { Spacing } from '@persona-id/design-tokens';
import { CircleHelpIcon } from '@persona-id/design-tokens/icons';
import { useCallback, useEffect } from 'react';
import { Controller, FormProvider, useFormState } from 'react-hook-form';
import { Prompt } from 'react-router';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import ReactSelect from 'dashboard/components/ReactSelect';
import { BirthdateMatchingFormSection } from 'dashboard/forms/FormReportTemplate/BirthdateMatchingFormSection';
import { REPORT_TEMPLATE_COUNTRY_CODE_OPTIONS } from 'dashboard/forms/FormReportTemplate/matchOptions';
import { NameMatchingFormSection } from 'dashboard/forms/FormReportTemplate/NameMatchingFormSection';
import { NameMatchingFormData } from 'dashboard/forms/FormReportTemplate/NameMatchingFormSection/NameMatchingFormSection';
import {
  isValidRecurringInterval,
  RecurringIntervalInput,
} from 'dashboard/forms/FormReportTemplate/RecurringIntervalInput';
import RecurringReportFormWrapper from 'dashboard/forms/FormReportTemplate/RecurringReportFormWrapper';
import {
  ReportTemplateFormRow,
  ReportTemplateFormRowField,
  ReportTemplateFormRowLabelWrapper,
} from 'dashboard/forms/FormReportTemplate/ReportTemplateFieldRow';
import ReportTemplateHeader from 'dashboard/forms/FormReportTemplate/ReportTemplateHeader';
import Section from 'dashboard/layouts/FormLayout/Section';
import { ColumnContainer, SoloColumn } from 'dashboard/layouts/ShowViewLayoutV2';
import { PoliticallyExposedPersonClass } from 'dashboard/models/report/politically-exposed-person';
import { BirthdateMatchType } from 'dashboard/models/report-template-version';
import Card from 'design-system/components/Card';
import Checkbox from 'design-system/components/Checkbox';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import Tooltip from 'design-system/components/Tooltip';
import { BASE } from 'design-system/variables/spacing';
import { FontWeight } from 'design-system/variables/text';
import { useFormSecurely } from 'lib/security/reactHookForm';

const ALL_PEP_CLASSES: PoliticallyExposedPersonClass[] = [
  'pep-class-1',
  'pep-class-2',
  'pep-class-3',
  'pep-class-4',
];
interface FormReportTemplatePoliticallyExposedPersonProps {
  canEdit: boolean;
  canEditConfig: boolean;
  submit: (data: ReportTemplatePoliticallyExposedPersonConfigFormData) => void;
  defaultValues: ReportTemplatePoliticallyExposedPersonConfigFormData;
  isLoadingPage: boolean;
  latestPublishedVersionId: string;
  helpCenterUrl?: string;
}

export const FormReportTemplatePoliticallyExposedPerson = ({
  canEdit,
  canEditConfig,
  submit,
  defaultValues,
  isLoadingPage,
  latestPublishedVersionId,
  helpCenterUrl,
}: FormReportTemplatePoliticallyExposedPersonProps) => {
  const form = useFormSecurely<ReportTemplatePoliticallyExposedPersonConfigFormData>({
    mode: 'onChange',
    defaultValues,
  });
  const { register, handleSubmit, control, setValue, getValues, reset, watch } = form;
  const { isSubmitting, isDirty, errors, isValid, isSubmitted } = useFormState({
    control,
  });
  const disabled = !canEdit || isLoadingPage || isSubmitting;
  const disabledConfig = disabled || !canEditConfig;

  const togglePepClass = useCallback(
    (toggledClass: PoliticallyExposedPersonClass) => {
      const enabledClasses = getValues('enabled-classes') as PoliticallyExposedPersonClass[];
      const newClasses = enabledClasses.includes(toggledClass)
        ? enabledClasses.filter(
            (pepClass: PoliticallyExposedPersonClass) => pepClass !== toggledClass,
          )
        : [...enabledClasses, toggledClass];
      setValue('enabled-classes', newClasses, { shouldDirty: true });
    },
    [getValues, setValue],
  );

  const toggleAll = useCallback(() => {
    const enabledClasses = getValues('enabled-classes') as PoliticallyExposedPersonClass[];
    setValue('enabled-classes', enabledClasses.length === 4 ? [] : ALL_PEP_CLASSES, {
      shouldDirty: true,
    });
  }, [getValues, setValue]);

  const watchedEnabledClasses = watch('enabled-classes');
  const enabledClassesError =
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    watchedEnabledClasses.length <= 0 ? 'Please select at least 1 PEP class' : false;
  const intervalSeconds = watch('interval-seconds');

  useEffect(() => {
    if (isSubmitted) {
      reset(getValues());
    }
  }, [isSubmitted, getValues, reset]);

  return (
    <>
      <Prompt
        when={isDirty && !isSubmitting}
        message='You have unsaved changes. Are you sure you want to leave?'
      />
      <form onSubmit={handleSubmit(submit)}>
        <ReportTemplateHeader
          saveDisabled={disabled || !isDirty || !isValid}
          reportTemplateVersionId={latestPublishedVersionId}
        />
        <ColumnContainer>
          <SoloColumn>
            <RecurringReportFormWrapper
              continuousEnabled={intervalSeconds != null}
              canEdit={false}
              configContent={
                <>
                  <Card>
                    <Card.Body>
                      <Section cols={1}>
                        <StyledLabel
                          size='lg'
                          color={disabled ? 'disabled' : 'primary'}
                          htmlFor='form-report-template-pep__name'
                        >
                          Template name
                        </StyledLabel>
                        <Spacer height={2} />
                        <Input
                          {...register('name', {
                            required: 'A template name is required',
                          })}
                          id='form-report-template-pep__name'
                          type='text'
                          placeholder='Name'
                          disabled={disabled}
                        />
                        {errors.name && <FormError>{errors.name.message}</FormError>}
                      </Section>
                    </Card.Body>
                  </Card>
                  <Card>
                    <Card.Body>
                      <StyledLabel size='lg'>PEP classes</StyledLabel>
                      <CheckboxContainer>
                        <Checkbox
                          id='form-report-template-pep__select-all'
                          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                          checked={watchedEnabledClasses.length === 4}
                          indeterminate={
                            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                            watchedEnabledClasses.length > 0 && watchedEnabledClasses.length < 4
                          }
                          onChange={toggleAll}
                          disabled={disabledConfig}
                        />
                        <Label
                          size='lg'
                          htmlFor='form-report-template-pep__select-all'
                          weight='bold'
                        >
                          Select all
                        </Label>
                      </CheckboxContainer>
                      <CheckboxContainer>
                        <Checkbox
                          id='form-report-template-pep__pep-class-1'
                          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                          checked={watchedEnabledClasses.includes('pep-class-1')}
                          onChange={() => {
                            togglePepClass('pep-class-1');
                          }}
                          disabled={disabledConfig}
                        />
                        <PepClassLabel size='md' htmlFor='form-report-template-pep__pep-class-1'>
                          <span>Class 1</span>
                          <Tooltip side='right' iconTrigger={<StyledCircleHelpIcon />}>
                            <TooltipWrapper>
                              High risk: Heads of state, members of the national executive, members
                              of the national legislatures, senior officials of military and law
                              enforcement, senior officials of national agencies, board members of
                              central banks, senior judiciary officials (national level),
                              top-ranking officials of political parties
                            </TooltipWrapper>
                          </Tooltip>
                        </PepClassLabel>
                      </CheckboxContainer>
                      <CheckboxContainer>
                        <Checkbox
                          id='form-report-template-pep__pep-class-2'
                          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                          checked={watchedEnabledClasses.includes('pep-class-2')}
                          onChange={() => {
                            togglePepClass('pep-class-2');
                          }}
                          disabled={disabledConfig}
                        />
                        <PepClassLabel size='md' htmlFor='form-report-template-pep__pep-class-2'>
                          <span>Class 2</span>
                          <Tooltip side='right' iconTrigger={<StyledCircleHelpIcon />}>
                            <TooltipWrapper>
                              Medium risk: Board members of International Organizations (HIOs) &
                              International Sports Federations, Ambassadors, high commissioners and
                              other top diplomatic positions, Members of the regional executive,
                              Members of the regional legislatures, Senior judiciary officials
                              (regional level){' '}
                            </TooltipWrapper>
                          </Tooltip>
                        </PepClassLabel>
                      </CheckboxContainer>
                      <CheckboxContainer>
                        <Checkbox
                          id='form-report-template-pep__pep-class-3'
                          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                          checked={watchedEnabledClasses.includes('pep-class-3')}
                          onChange={() => {
                            togglePepClass('pep-class-3');
                          }}
                          disabled={disabledConfig}
                        />
                        <PepClassLabel size='md' htmlFor='form-report-template-pep__pep-class-3'>
                          <span>Class 3</span>
                          <Tooltip side='right' iconTrigger={<StyledCircleHelpIcon />}>
                            <TooltipWrapper>
                              Medium risk: Senior management and board of directors of state-owned
                              businesses and organizations
                            </TooltipWrapper>
                          </Tooltip>
                        </PepClassLabel>
                      </CheckboxContainer>
                      <CheckboxContainer>
                        <Checkbox
                          id='form-report-template-pep__pep-class-4'
                          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                          checked={watchedEnabledClasses.includes('pep-class-4')}
                          onChange={() => {
                            togglePepClass('pep-class-4');
                          }}
                          disabled={disabledConfig}
                        />
                        <PepClassLabel size='md' htmlFor='form-report-template-pep__pep-class-4'>
                          <span>Class 4</span>
                          <Tooltip side='right' iconTrigger={<StyledCircleHelpIcon />}>
                            <TooltipWrapper>
                              Low risk: Mayors and members of local government (sub-regional level)
                            </TooltipWrapper>
                          </Tooltip>
                        </PepClassLabel>
                      </CheckboxContainer>
                      {enabledClassesError && <FormError>{enabledClassesError}</FormError>}
                    </Card.Body>
                  </Card>
                  <Card>
                    <Card.Body>
                      <StyledLabel size='lg'>Match filters</StyledLabel>
                      <ReportTemplateFormRow>
                        <ReportTemplateFormRowLabelWrapper>
                          <Label size='lg' weight='bold'>
                            Allow Relatives & Close Associates
                          </Label>
                          <Paragraph>
                            Allow matches for persons who are related or closely associated to PEPs
                          </Paragraph>
                        </ReportTemplateFormRowLabelWrapper>
                        <ReportTemplateFormRowField>
                          <Controller
                            control={control}
                            name='associates-enabled'
                            render={({ field: { onChange, value } }) => (
                              <ToggleSlider
                                onChange={onChange}
                                value={value}
                                labelOn='Enabled'
                                labelOff='Disabled'
                                disabled={disabledConfig}
                              />
                            )}
                          />
                        </ReportTemplateFormRowField>
                      </ReportTemplateFormRow>
                      <ReportTemplateFormRow>
                        <ReportTemplateFormRowLabelWrapper>
                          <Label size='lg' weight='bold'>
                            Allow sources with no URLs
                          </Label>
                          <Paragraph>
                            Allow matches that do not have a URL associated with them
                          </Paragraph>
                        </ReportTemplateFormRowLabelWrapper>
                        <ReportTemplateFormRowField>
                          <Controller
                            control={control}
                            name='internal-sources-enabled'
                            render={({ field: { onChange, value } }) => (
                              <ToggleSlider
                                onChange={onChange}
                                value={value}
                                labelOn='Enabled'
                                labelOff='Disabled'
                                disabled={disabledConfig}
                              />
                            )}
                          />
                        </ReportTemplateFormRowField>
                      </ReportTemplateFormRow>
                      <ReportTemplateFormRow noBorder={true}>
                        <ReportTemplateFormRowLabelWrapper>
                          <Label size='lg' weight='bold'>
                            Allowed countries
                          </Label>
                          <Paragraph>
                            Filter by the country of the government position title
                          </Paragraph>
                        </ReportTemplateFormRowLabelWrapper>
                        <ReportTemplateFormRowField width='100%'>
                          <Controller
                            control={control}
                            name='country-codes'
                            render={({ field: { onChange, onBlur, value } }) => (
                              <ReactSelect
                                onChange={onChange}
                                onBlur={onBlur}
                                value={value}
                                options={REPORT_TEMPLATE_COUNTRY_CODE_OPTIONS}
                                disabled={disabledConfig}
                                isMulti={true}
                                placeholder='None (all countries allowed)'
                              />
                            )}
                          />
                        </ReportTemplateFormRowField>
                      </ReportTemplateFormRow>
                    </Card.Body>
                  </Card>
                  <FormProvider {...form}>
                    <Card>
                      <Card.Body>
                        <StyledLabel size='lg'>Name matching</StyledLabel>
                        <Spacer height={2} />
                        <NameMatchingFormSection
                          defaultValues={defaultValues}
                          disabled={disabledConfig}
                        />
                      </Card.Body>
                    </Card>
                    <Card>
                      <Card.Body>
                        <StyledLabel size='lg'>Birthdate matching</StyledLabel>
                        <Spacer height={2} />
                        <BirthdateMatchingFormSection
                          availableOptions={[
                            BirthdateMatchType.DayExact,
                            BirthdateMatchType.YearExact,
                            BirthdateMatchType.PlusOrMinusOneYear,
                          ]}
                          disabled={disabledConfig}
                        />
                      </Card.Body>
                    </Card>
                  </FormProvider>
                  {helpCenterUrl && (
                    <Card>
                      <Card.Body>
                        <StyledLabel size='lg'>Additional resources</StyledLabel>
                        <span>
                          Learn more about configuring this report on the{' '}
                          <a href={helpCenterUrl} target='_blank' rel='noreferrer'>
                            help center
                          </a>
                          .
                        </span>
                      </Card.Body>
                    </Card>
                  )}
                </>
              }
              recurringContent={
                <Card>
                  <Card.Body>
                    <StyledLabel size='lg'>Continuous monitoring</StyledLabel>
                    <ReportTemplateFormRow noBorder={true}>
                      <ReportTemplateFormRowLabelWrapper>
                        <Label size='lg' weight='bold'>
                          Recurring interval
                        </Label>
                        <Paragraph>Set a recurring cadence to run this report</Paragraph>
                      </ReportTemplateFormRowLabelWrapper>
                      <ReportTemplateFormRowField>
                        <Controller
                          control={control}
                          name='interval-seconds'
                          rules={{
                            validate: (value) =>
                              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                              isValidRecurringInterval(value) ||
                              'Please enter an interval between 1 and 365 days',
                          }}
                          render={({ field: { onChange, onBlur, value } }) => (
                            <RecurringIntervalInput
                              id='form-report-template-pep__interval-seconds'
                              name='interval-seconds'
                              disabled={true}
                              onChange={onChange}
                              onBlur={onBlur}
                              value={value}
                            />
                          )}
                        />
                      </ReportTemplateFormRowField>
                    </ReportTemplateFormRow>
                    <ReportTemplateFormRow noBorder={true} initialColumnWidth='70%'>
                      <ReportTemplateFormRowLabelWrapper>
                        <Label size='lg' weight='bold'>
                          Pause continuous monitoring upon match
                        </Label>
                        <Paragraph>
                          When enabled: if there is a match, recurrence will pause until all matches
                          are dismissed.
                        </Paragraph>
                        <Paragraph>
                          When disabled: reports will rerun regardless of match. This will likely
                          result in the same matches being surfaced again, unless they have been
                          dismissed.
                        </Paragraph>
                      </ReportTemplateFormRowLabelWrapper>
                      <ReportTemplateFormRowField>
                        <Controller
                          control={control}
                          name='always-recur'
                          render={({ field: { onChange, value } }) => (
                            <ToggleSlider
                              onChange={onChange}
                              value={!value}
                              labelOn='Enabled'
                              labelOff='Disabled'
                              disabled={true}
                            />
                          )}
                        />
                      </ReportTemplateFormRowField>
                    </ReportTemplateFormRow>
                  </Card.Body>
                </Card>
              }
            />
          </SoloColumn>
        </ColumnContainer>
      </form>
    </>
  );
};

export interface ReportTemplatePoliticallyExposedPersonConfigFormData extends NameMatchingFormData {
  name: string;
  'interval-seconds'?: number;
  'always-recur'?: boolean;
  'enabled-classes'?: PoliticallyExposedPersonClass[];
  'associates-enabled'?: boolean;
  'internal-sources-enabled'?: boolean;
  'country-codes'?: string[];
  'birthdate-match-type': BirthdateMatchType;
  'data-source': string;
}

const CheckboxContainer = styled.div`
  display: grid;
  grid-column-gap: ${Spacing[3]};
  grid-template-columns: 20px 1fr;
  align-items: center;
  padding: ${BASE * 1.25}px 0;
`;

const StyledLabel = styled(Label)`
  font-weight: ${FontWeight.Bold};
`;

const PepClassLabel = styled(Label)`
  display: flex;
  align-items: center;
`;

const StyledCircleHelpIcon = styled(CircleHelpIcon)`
  margin-left: 8px;
  color: ${({ theme }) => theme.color.text.tertiary};
`;

const TooltipWrapper = styled.span`
  max-width: 400px;
`;
